import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Paper, Typography, TextField, MenuItem, Box } from '@mui/material';
import { createPipeline } from '@/api/pipeline';

const sequenceOptions = [10, 30, 50, 100];
const structureOptions = [1, 3, 5];

const PipelineForm: React.FC = () => {
  const [name, setName] = useState('');
  const [pdbFile, setPdbFile] = useState<File | null>(null);
  const [pdbContent, setPdbContent] = useState<string>('');
  const [numSequences, setNumSequences] = useState(10);
  const [numToBuild, setNumToBuild] = useState('All');
  const [numStructures, setNumStructures] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setPdbFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setPdbContent(event.target?.result as string);
      };
      reader.onerror = () => {
        setError('Failed to read PDB file.');
        setPdbContent('');
      };
      reader.readAsText(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !pdbFile || !pdbContent) {
      setError('Name and PDB file are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = {
        name,
        pipeline_type: 'esmif_colabfold',
        params: {
          num_designs: numSequences,
          temperature: 0.5,
          num_to_build: numToBuild,
          num_structures: numStructures
        },
        input_pdb: pdbContent
      };
      await createPipeline(data);
      navigate('/pipelines');
    } catch (err) {
      console.error('Error creating pipeline:', err);
      setError('Failed to create pipeline.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 600, margin: '0 auto' }}>
      <Typography variant="h5" gutterBottom>New Pipeline: ESMIF → ColabFold</Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Pipeline Name"
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <Button variant="contained" component="label" sx={{ mt: 2, mb: 2 }}>
          Upload PDB File
          <input type="file" accept=".pdb" hidden onChange={handleFileChange} />
        </Button>
        {pdbFile && <Typography variant="body2">Selected: {pdbFile.name}</Typography>}
        <TextField
          select
          label="# of sequences to generate"
          value={numSequences}
          onChange={e => setNumSequences(Number(e.target.value))}
          fullWidth
          margin="normal"
        >
          {sequenceOptions.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="# of sequences to build"
          value={numToBuild}
          onChange={e => setNumToBuild(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Type a number or 'All'"
        />
        <TextField
          select
          label="# of structures per sequence"
          value={numStructures}
          onChange={e => setNumStructures(Number(e.target.value))}
          fullWidth
          margin="normal"
        >
          {structureOptions.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
        {error && <Typography color="error">{error}</Typography>}
        <Box sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Pipeline'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default PipelineForm; 